import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import styled from "styled-components";
import { Tooltip } from "react-tooltip";
import { PitchTrackerFrame, PitchTrackerSvg } from "../../tournametns/shared/ui/EventPitchTrackerStyles";
import EventPitchBaseLayer from "../../tournametns/shared/ui/EventPitchBaseLayer";
import { buildShadowTeamSlots, MAX_SHADOW_TEAM_PLAYERS_PER_SLOT, MAX_PITCH_PLAYERS_VISIBLE_PER_SLOT } from "../utils/formation";
import { filterShadowTeamSearchResultsForSlot } from "../utils/playerSearch";
import { getMediaUrl } from "../../../helpers";
import { ReactComponent as TrashIconSvg } from "../../../images/ic_bin.svg";

const FIELD_WIDTH = 630;
const FIELD_HEIGHT = 860;

const PitchWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.linesOpacity};
  border-radius: 10px;
  padding: 16px;
`;

const PitchTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const PitchTitle = styled.p`
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiBold";
  font-size: 16px;
  margin-bottom: 0;
`;

const ExportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 13px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  background: rgba(255, 255, 255, 0.04);
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiMedium";
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.green};
    color: ${({ theme }) => theme.green};
    background: ${({ theme }) => theme.green === '#2363F7' ? 'rgba(35, 99, 247, 0.06)' : 'rgba(0, 200, 80, 0.05)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  svg {
    flex-shrink: 0;
  }
`;

const PitchCanvas = styled.div`
  width: 100%;
  max-width: 660px;
  margin: 0 auto;
  position: relative;
`;

const PitchFrame = styled(PitchTrackerFrame)`
  width: 100%;
  margin: 0 auto;
  aspect-ratio: ${FIELD_WIDTH} / ${FIELD_HEIGHT};
  position: relative;
`;

/* ── Quick-add popover ──────────────────────────────── */

const QuickAddOverlay = styled.div`
  position: absolute;
  z-index: 300;
  width: 240px;
  background: ${({ theme }) => theme.dropdownBg};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 12px;
  box-shadow: 0 10px 36px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 10px;
`;

const QuickAddSlotLabel = styled.p`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 11px;
  margin: 0 0 8px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const QuickAddInput = styled.input`
  width: 100%;
  background: ${({ theme }) => theme.inputBg};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiRegular";
  font-size: 13px;
  padding: 8px 10px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(150, 170, 210, 0.45);
  }

  &:focus {
    border-color: ${({ theme }) => theme.green};
  }
`;

const QuickAddResults = styled.div`
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  max-height: 192px;
  overflow-y: auto;
`;

const QuickAddResultItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border-radius: 7px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.green === '#2363F7' ? 'rgba(35, 99, 247, 0.06)' : 'rgba(255, 255, 255, 0.07)'};
  }
`;

const QuickAddPlayerAvatar = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: ${({ theme }) => theme.cardBgAlt};
`;

const QuickAddPlayerName = styled.span`
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiMedium";
  font-size: 12px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const QuickAddPlayerNat = styled.span`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 10px;
  flex-shrink: 0;
`;

const QuickAddHint = styled.p`
  color: rgba(150, 170, 210, 0.5);
  font-family: "SatoshiRegular";
  font-size: 11px;
  text-align: center;
  margin: 10px 0 4px;
`;

const QuickAddFooter = styled.button`
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 6px;
  background: none;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 7px;
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 11px;
  cursor: pointer;
  text-align: center;

  &:hover {
    border-color: ${({ theme }) => theme.green};
    color: ${({ theme }) => theme.green};
  }
`;

const PitchHint = styled.p`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 13px;
  margin-top: 12px;
`;

const RosterSection = styled.section`
  margin-top: 18px;
`;

const RosterTitle = styled.h3`
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiBold";
  font-size: 15px;
  margin: 0 0 6px;
`;

const RosterHint = styled.p`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 12px;
  margin: 0 0 14px;
`;

/* ── Roster line sections ──────────────────────────────── */

const RosterLineSection = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const RosterLineSectionLabel = styled.div`
  font-size: 11px;
  font-family: "SatoshiMedium";
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.blueish2};
  margin-bottom: 8px;
  padding-left: 2px;
`;

const RosterLineGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $isGk }) =>
    $isGk ? "minmax(170px, 320px)" : "repeat(auto-fit, minmax(170px, 1fr))"};
  gap: 10px;
`;

const PosCard = styled.div`
  background: ${({ theme }) => theme.linesOpacity};
  border: 0.5px solid ${({ $isDragTarget, theme }) =>
    $isDragTarget ? theme.green : theme.borderColor};
  border-radius: 12px;
  overflow: hidden;
  opacity: ${({ $isDragSource }) => ($isDragSource ? 0.45 : 1)};
  box-shadow: ${({ $isDragTarget, theme }) =>
    $isDragTarget ? `0 0 0 1.5px ${theme.green}55` : "none"};
  transition:
    border-color 0.15s ease,
    opacity 0.12s ease,
    box-shadow 0.12s ease;

  &:hover {
    border-color: ${({ $isDragTarget, theme }) =>
      $isDragTarget ? theme.green : theme.blueish2};
  }
`;

const PosCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 8px;
  border-bottom: 0.5px solid ${({ theme }) => theme.borderColor};
`;

const PosBadge = styled.span`
  font-size: 12px;
  font-family: "SatoshiBold";
  padding: 3px 10px;
  border-radius: 20px;
  background: ${({ theme }) => theme.green};
  color: ${({ theme }) => theme.textOnAccent};
  letter-spacing: 0.03em;
`;

const PosCount = styled.span`
  font-size: 11px;
  font-family: "SatoshiRegular";
  color: ${({ theme }) => theme.blueish2};
  display: flex;
  align-items: center;
  gap: 4px;
  font-style: ${({ $empty }) => ($empty ? "italic" : "normal")};
`;

const PosCountDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #7bc950;
  flex-shrink: 0;
`;

const PosCardBody = styled.div`
  padding: 8px 10px 10px;
`;

const ChipTrashBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.blueish2};
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s ease, color 0.12s ease, background 0.12s ease;
  padding: 0;
  margin-left: auto;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    color: #ff6b6b;
    background: rgba(255, 80, 80, 0.12);
  }
`;

const PlayerChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  margin-bottom: 4px;
  background: ${({ $isDropTarget, theme }) =>
    $isDropTarget
      ? (theme.green === '#2363F7' ? 'rgba(35, 99, 247, 0.12)' : 'rgba(173, 196, 39, 0.16)')
      : (theme.green === '#2363F7' ? theme.cardBgAlt : 'rgba(255, 255, 255, 0.06)')};
  cursor: grab;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.35 : 1)};
  box-shadow: ${({ $isDropTarget, theme }) =>
    $isDropTarget ? `inset 0 0 0 1px ${theme.green}` : "none"};
  transition:
    background 0.12s ease,
    opacity 0.12s ease,
    box-shadow 0.12s ease;
  user-select: none;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.green === '#2363F7' ? 'rgba(35, 99, 247, 0.08)' : 'rgba(255, 255, 255, 0.09)'};

    ${ChipTrashBtn} {
      opacity: 1;
    }
  }

  &:active {
    cursor: grabbing;
  }
`;

const PlayerChipAvatar = styled.div`
  position: relative;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.cardBgAlt};
  color: ${({ theme }) => theme.white};
  font-size: 10px;
  font-family: "SatoshiBold";
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.borderColor};
  overflow: hidden;
`;

const PlayerChipInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PlayerChipName = styled.a`
  display: block;
  font-size: 12px;
  font-family: "SatoshiMedium";
  color: ${({ theme }) => theme.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
  line-height: 1.3;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.green};
  }
`;

const PlayerChipClub = styled.span`
  display: block;
  font-size: 10px;
  font-family: "SatoshiRegular";
  color: ${({ theme }) => theme.blueish2};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const RosterEmptyState = styled.div`
  padding: 10px 8px;
  text-align: center;
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 11px;
`;

const RosterAddBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  margin-top: 6px;
  padding: 6px;
  border-radius: 6px;
  border: 0.5px dashed ${({ theme }) => theme.blueish2};
  background: transparent;
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 11px;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover {
    background: ${({ theme }) => theme.green === '#2363F7' ? 'rgba(35, 99, 247, 0.06)' : 'rgba(255, 255, 255, 0.05)'};
    color: ${({ theme }) => theme.white};
    border-style: solid;
  }
`;

/* ── Tooltip card ──────────────────────────────────── */

const TooltipCard = styled.div`
  padding: 12px 14px;
  min-width: 200px;
  max-width: 248px;
  background: ${({ theme }) => theme.dropdownBg};
  border: 0.5px solid ${({ theme }) => theme.borderColor};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.12);
`;

const TooltipHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const TooltipPlayerAvatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: ${({ theme }) => theme.cardBgAlt};
`;

const TooltipPlayerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const TooltipPlayerName = styled.p`
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiBold";
  font-size: 13px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TooltipNationality = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const TooltipNatFlag = styled.img`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const TooltipNatName = styled.span`
  color: ${({ theme }) => theme.textMuted};
  font-family: "SatoshiRegular";
  font-size: 11px;
`;

const TooltipDivider = styled.hr`
  border: none;
  border-top: 0.5px solid ${({ theme }) => theme.borderSubtle};
  margin: 8px 0;
`;

const TooltipStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const TooltipStatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const TooltipStatLabel = styled.span`
  color: ${({ theme }) => theme.textMuted};
  font-family: "SatoshiRegular";
  font-size: 11px;
`;

const TooltipStatValue = styled.span`
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiMedium";
  font-size: 11px;
  text-align: right;
`;

const TooltipStatValueGreen = styled(TooltipStatValue)`
  color: ${({ theme }) => theme.green};
`;

const TooltipLoadingText = styled.p`
  color: ${({ theme }) => theme.textMuted};
  font-family: "SatoshiRegular";
  font-size: 11px;
  margin: 2px 0 0;
  text-align: center;
`;

const TooltipSectionLabel = styled.p`
  color: ${({ theme }) => theme.textMuted};
  font-family: "SatoshiRegular";
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 0 0 5px;
`;

const TooltipSeasonStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  margin-top: 2px;
`;

const TooltipSeasonStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: ${({ theme }) => theme.cardBgAlt};
  border-radius: 8px;
  padding: 6px 4px;
`;

const TooltipSeasonStatValue = styled.span`
  color: ${({ $green, theme }) => ($green ? theme.green : theme.white)};
  font-family: "SatoshiBold";
  font-size: 14px;
  line-height: 1;
`;

const TooltipSeasonStatLabel = styled.span`
  color: ${({ theme }) => theme.textMuted};
  font-family: "SatoshiRegular";
  font-size: 10px;
`;


const DEFAULT_PLAYER_PHOTO =
  "https://api-v0.smrtstats.com/media/pics/players/1320301.png";

const getPlayerPhoto = (player) => {
  const photoEntry = Array.isArray(player?.photos)
    ? player.photos.find((photo) => photo?.full || photo?.image)
    : null;

  return photoEntry?.full || photoEntry?.image || player?.photo || DEFAULT_PLAYER_PHOTO;
};

const getAge = (birthDate) => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age > 0 && age < 100 ? age : null;
};

const getContractYear = (contractUntil) => {
  if (!contractUntil) return null;
  const year = new Date(contractUntil).getFullYear();
  return Number.isFinite(year) && year > 2000 ? year : null;
};

const findPlayerById = (slotPlayersMap, playerId) => {
  if (!playerId) return null;
  for (const players of Object.values(slotPlayersMap)) {
    const found = (players || []).find((p) => p?.id === playerId);
    if (found) return found;
  }
  return null;
};

const getPlayerFullName = (player) =>
  `${String(player?.name ?? "").trim()} ${String(player?.surname ?? "").trim()}`.trim() ||
  `#${player?.id}`;

const getPlayerClubName = (player) =>
  String(player?.clubName ?? player?.team?.name ?? "").trim() || null;

const getPlayerInitials = (player) => {
  const first = String(player?.name ?? "").trim()[0] ?? "";
  const last = String(player?.surname ?? "").trim()[0] ?? "";
  return (first + last).toUpperCase() || "?";
};

const findNearestSlot = (svgX, svgY, slots, threshold = 42) => {
  let nearest = null;
  let minDist = Infinity;
  for (const slot of slots) {
    const cx = (slot.x * FIELD_WIDTH) / 100;
    const cy = (slot.y * FIELD_HEIGHT) / 100;
    const dist = Math.sqrt((svgX - cx) ** 2 + (svgY - cy) ** 2);
    if (dist < minDist) {
      minDist = dist;
      nearest = slot;
    }
  }
  return minDist <= threshold ? nearest : null;
};

const canDropPlayerInSlot = () => true;

const SLOT_RADIUS = 22;

const getSlotRadius = () => SLOT_RADIUS;

const getSlotLabelFontSize = (slot) => {
  if ((slot.shortLabel || "").length >= 4) {
    return 10;
  }

  if ((slot.shortLabel || "").length === 3) {
    return 11.5;
  }

  return 13;
};

const getSlotTitle = (t, slot) =>
  t("shadowTeam.slotLabel", {
    shortLabel: slot?.shortLabel || "GK",
    line: slot?.lineIndex < 0 ? "GK" : (slot?.lineIndex ?? 0) + 1,
    position: (slot?.positionIndex ?? 0) + 1,
  });

const getSlotLineLabel = (t, slot) =>
  t("shadowTeam.lineLabel", {
    line: slot?.lineIndex < 0 ? "GK" : (slot?.lineIndex ?? 0) + 1,
  });

const getSlotPositionLabel = (t, slot) =>
  t("shadowTeam.positionLabel", {
    position: (slot?.positionIndex ?? 0) + 1,
  });

const getSlotStatusLabel = (t, players) =>
  players.length ? t("shadowTeam.assignedPlayersCount", { count: players.length }) : null;

const BAND_DISPLAY_LABELS = {
  gk: "Goalkeeper",
  defense: "Defensive",
  defensiveMid: "Defensive Midfield",
  midfield: "Midfield",
  attackingMid: "Attacking Midfield",
  attack: "Attack",
};

const getRosterLineSectionLabel = (lineIndex, band) => {
  if (lineIndex < 0) return "Goalkeeper Line";
  const bandLabel = BAND_DISPLAY_LABELS[band] || band;
  return `Line ${lineIndex + 1} \u2014 ${bandLabel}`;
};

const ShadowTeamPitch = ({
  formationName,
  formationLines,
  slotPlayersMap = {},
  onAddPlayer,
  playerStatsCache = {},
  onPlayerHover,
  onMovePlayer,
  onReorderPlayers,
  onRemovePlayer,
  // quick-add
  quickAddSlot = null,
  quickAddResults = [],
  quickAddLoading = false,
  onEmptySlotClick,
  onQuickAddSearch,
  onQuickAddSelect,
  onQuickAddClose,
  // export
  onExport = null,
  isExporting = false,
  tutorialPitchRef = null,
  tutorialExportRef = null,
  tutorialRosterRef = null,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const _isLight = theme.green === "#2363F7";
  const pitchAccent = _isLight ? "rgba(35,99,247" : "rgba(204,255,0";

  const slots = useMemo(() => buildShadowTeamSlots(formationLines), [formationLines]);

  // ── Quick-add popover state ──────────────────────────────────────────
  const popoverRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const [quickSearchValue, setQuickSearchValue] = useState("");

  useEffect(() => {
    setQuickSearchValue("");
  }, [quickAddSlot?.key]);

  // Close popover on click-outside or Escape
  useEffect(() => {
    if (!quickAddSlot) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onQuickAddClose?.();
    };
    const handleMouseDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onQuickAddClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [quickAddSlot, onQuickAddClose]);

  const handleQuickSearchInput = useCallback(
    (e) => {
      const { value } = e.target;
      setQuickSearchValue(value);
      clearTimeout(searchDebounceRef.current);
      if (value.trim().length >= 2) {
        searchDebounceRef.current = setTimeout(() => {
          onQuickAddSearch?.(value.trim());
        }, 300);
      }
    },
    [onQuickAddSearch]
  );

  const filteredQuickAddResults = useMemo(
    () => filterShadowTeamSearchResultsForSlot(quickAddResults, quickAddSlot, quickSearchValue),
    [quickAddResults, quickAddSlot, quickSearchValue]
  );

  // ── Roster card drag-and-drop state ─────────────────────────────────
  const [rosterDrag, setRosterDrag] = useState(null); // { player, sourceSlotKey }
  const [rosterDragOver, setRosterDragOver] = useState(null); // target slot key
  const [rosterDragOverPlayer, setRosterDragOverPlayer] = useState(null); // { slotKey, playerId }

  const clearRosterDragState = useCallback(() => {
    setRosterDrag(null);
    setRosterDragOver(null);
    setRosterDragOverPlayer(null);
  }, []);

  const handleRosterDragStart = useCallback((e, player, sourceSlotKey) => {
    setRosterDragOverPlayer(null);
    setRosterDrag({ player, sourceSlotKey });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(player?.id ?? ""));
  }, []);

  const handleRosterDragEnd = useCallback(() => {
    clearRosterDragState();
  }, [clearRosterDragState]);

  const handleRosterDragOver = useCallback(
    (e, slotKey) => {
      const targetSlot = slots.find((s) => s.key === slotKey);
      if (
        rosterDrag &&
        slotKey !== rosterDrag.sourceSlotKey &&
        canDropPlayerInSlot(rosterDrag.player, targetSlot)
      ) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setRosterDragOver(slotKey);
        setRosterDragOverPlayer(null);
        return;
      }

      setRosterDragOver(null);
    },
    [rosterDrag, slots]
  );

  const handleRosterDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setRosterDragOver(null);
    }
  }, []);

  const handleRosterPlayerDragEnter = useCallback(
    (slotKey, playerId) => {
      if (
        !rosterDrag ||
        slotKey !== rosterDrag.sourceSlotKey ||
        rosterDrag.player?.id === playerId
      ) {
        return;
      }

      setRosterDragOver(null);
      setRosterDragOverPlayer({ slotKey, playerId });
    },
    [rosterDrag]
  );

  const handleRosterPlayerDragOver = useCallback(
    (e, slotKey, playerId) => {
      if (
        !rosterDrag ||
        slotKey !== rosterDrag.sourceSlotKey ||
        rosterDrag.player?.id === playerId
      ) {
        return;
      }

      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      if (
        rosterDragOverPlayer?.slotKey !== slotKey ||
        rosterDragOverPlayer?.playerId !== playerId
      ) {
        setRosterDragOver(null);
        setRosterDragOverPlayer({ slotKey, playerId });
      }
    },
    [rosterDrag, rosterDragOverPlayer]
  );

  const handleRosterPlayerDragLeave = useCallback((e, slotKey, playerId) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setRosterDragOverPlayer((currentValue) => {
        if (
          currentValue?.slotKey === slotKey &&
          currentValue?.playerId === playerId
        ) {
          return null;
        }

        return currentValue;
      });
    }
  }, []);

  const handleRosterPlayerDrop = useCallback(
    (e, targetSlot, targetPlayer) => {
      e.preventDefault();
      e.stopPropagation();

      if (!rosterDrag) {
        clearRosterDragState();
        return;
      }

      if (rosterDrag.sourceSlotKey === targetSlot.key) {
        const draggedPlayerId = rosterDrag.player?.id;
        const targetPlayerId = targetPlayer?.id;

        if (draggedPlayerId && targetPlayerId && draggedPlayerId !== targetPlayerId) {
          onReorderPlayers?.(targetSlot, draggedPlayerId, targetPlayerId);
        }

        clearRosterDragState();
        return;
      }

      if (canDropPlayerInSlot(rosterDrag.player, targetSlot)) {
        onMovePlayer?.(rosterDrag.player, targetSlot);
      }
      clearRosterDragState();
    },
    [clearRosterDragState, onMovePlayer, onReorderPlayers, rosterDrag]
  );

  const handleRosterDrop = useCallback(
    (e, targetSlot) => {
      e.preventDefault();
      if (!rosterDrag || rosterDrag.sourceSlotKey === targetSlot.key) {
        clearRosterDragState();
        return;
      }
      if (canDropPlayerInSlot(rosterDrag.player, targetSlot)) {
        onMovePlayer?.(rosterDrag.player, targetSlot);
      }
      clearRosterDragState();
    },
    [clearRosterDragState, rosterDrag, onMovePlayer]
  );

  // ── SVG pitch drag state ─────────────────────────────────────────────
  const svgRef = useRef(null);
  const dragStartRef = useRef(null); // { clientX, clientY, sourceSlotKey, player }
  const isDraggingRef = useRef(false);
  const wasJustDraggingRef = useRef(false);
  const [dragState, setDragState] = useState(null); // { sourceSlotKey, player, x, y }
  const [dragTargetKey, setDragTargetKey] = useState(null);

  // ── SVG list reorder drag state ──────────────────────────────────────
  // Tracks intra-slot row drag (reorder players within a slot).
  // Active while a row is being dragged but the slot circle is NOT dragged.
  const listReorderDragRef = useRef(null);
  // { slot, slotKey, player, fromIdx, targetIdx }
  const [listReorderDrag, setListReorderDrag] = useState(null);

  const screenToSvg = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgPt = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: svgPt.x, y: svgPt.y };
  }, []);

  const handleSlotMouseDown = useCallback((e, slot, primaryPlayer) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    dragStartRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      sourceSlotKey: slot.key,
      player: primaryPlayer,
    };
    isDraggingRef.current = false;
  }, []);

  // Row mousedown — ambiguous until mouse moves:
  //   • primarily vertical → intra-slot list reorder
  //   • horizontal / toward another slot → slot-to-slot drag (original behaviour)
  const handleRowMouseDown = useCallback((e, slot, player, fromIdx, players) => {
    if (e.button !== 0) return;
    e.stopPropagation();

    // Always prime slot-to-slot drag (same as handleSlotMouseDown)
    dragStartRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      sourceSlotKey: slot.key,
      player,
    };
    isDraggingRef.current = false;

    if (players.length <= 1) {
      // Single player in slot — slot-to-slot only, no list reorder candidate
      return;
    }

    // Multiple players — also prime list reorder, pending direction decision
    listReorderDragRef.current = {
      slot, slotKey: slot.key, player, players, fromIdx, targetIdx: fromIdx,
      pending: true,  // direction not yet decided
    };
  }, []);  // no deps needed — reads/writes refs only

  const handleSvgMouseMove = useCallback((e) => {
    // ── Pending direction decision (row drag with multiple players) ──
    if (listReorderDragRef.current?.pending) {
      if (!dragStartRef.current) return;
      const dx = e.clientX - dragStartRef.current.clientX;
      const dy = e.clientY - dragStartRef.current.clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 8) return; // wait for intentional movement

      if (Math.abs(dy) > Math.abs(dx)) {
        // Primarily vertical → commit to list reorder, cancel slot-to-slot
        listReorderDragRef.current = { ...listReorderDragRef.current, pending: false };
        setListReorderDrag({
          slot: listReorderDragRef.current.slot,
          slotKey: listReorderDragRef.current.slotKey,
          player: listReorderDragRef.current.player,
          fromIdx: listReorderDragRef.current.fromIdx,
          targetIdx: listReorderDragRef.current.fromIdx,
        });
        dragStartRef.current = null;
      } else {
        // Primarily horizontal / toward another slot → slot-to-slot, cancel list reorder
        listReorderDragRef.current = null;
        // dragStartRef stays set; slot-to-slot activates in the branch below
      }
      return;
    }

    // ── List reorder drag (committed) ──
    if (listReorderDragRef.current) {
      const { slotKey } = listReorderDragRef.current;
      const reorderSlot = slots.find((s) => s.key === slotKey);
      if (reorderSlot) {
        const { y: svgY } = screenToSvg(e.clientX, e.clientY);
        const slotCy = (reorderSlot.y * FIELD_HEIGHT) / 100;
        const isGoalkeeperSlot = reorderSlot.lineIndex < 0;
        const isBottomSlot = reorderSlot.y > 80;
        const listPlacement = isGoalkeeperSlot ? "below" : isBottomSlot ? "above" : "below";
        const pillY = slotCy + SLOT_RADIUS + 5;
        const pillH = 17;
        const ROW_H = 20;
        const playerCount = listReorderDragRef.current.players?.length ?? 1;
        const listStartY = listPlacement === "above"
          ? slotCy - SLOT_RADIUS - 8 - playerCount * ROW_H
          : pillY + pillH + (isGoalkeeperSlot ? 4 : 6);
        const relY = svgY - listStartY;
        const rawIdx = Math.round(relY / ROW_H);
        const maxIdx = playerCount - 1;
        const targetIdx = Math.max(0, Math.min(rawIdx, maxIdx));
        listReorderDragRef.current = { ...listReorderDragRef.current, targetIdx };
        setListReorderDrag((prev) => prev ? { ...prev, targetIdx } : null);
      }
      return;
    }

    // ── Slot-to-slot drag ──
    if (!dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.clientX;
    const dy = e.clientY - dragStartRef.current.clientY;
    if (!isDraggingRef.current && Math.sqrt(dx * dx + dy * dy) < 10) return;

    isDraggingRef.current = true;
    const { x, y } = screenToSvg(e.clientX, e.clientY);

    setDragState({
      sourceSlotKey: dragStartRef.current.sourceSlotKey,
      player: dragStartRef.current.player,
      x,
      y,
    });

    const nearest = findNearestSlot(x, y, slots, 42);
    setDragTargetKey(
      nearest &&
      nearest.key !== dragStartRef.current.sourceSlotKey &&
      canDropPlayerInSlot(dragStartRef.current.player, nearest)
        ? nearest.key
        : null
    );
  }, [screenToSvg, slots]);

  const handleSvgMouseUp = useCallback((e) => {
    // ── List reorder drop (or pending cancelled on mouseup = treated as click) ──
    if (listReorderDragRef.current) {
      const { slot, player, players, fromIdx, targetIdx, pending } = listReorderDragRef.current;
      listReorderDragRef.current = null;
      setListReorderDrag(null);

      if (pending) {
        // Direction was never committed — treat as a simple click, clear drag state
        dragStartRef.current = null;
        isDraggingRef.current = false;
        setDragState(null);
        setDragTargetKey(null);
        return;
      }

      wasJustDraggingRef.current = true;
      if (fromIdx !== targetIdx) {
        const targetPlayer = Array.isArray(players) ? players[targetIdx] : null;
        const targetPlayerId = targetPlayer?.id ?? null;
        if (targetPlayerId !== null) {
          onReorderPlayers?.(slot, player?.id, targetPlayerId);
        }
      }
      return;
    }

    // ── Slot-to-slot drop ──
    const wasDragging = isDraggingRef.current;
    const start = dragStartRef.current;

    dragStartRef.current = null;
    isDraggingRef.current = false;
    setDragState(null);
    setDragTargetKey(null);

    if (!wasDragging || !start) return;

    wasJustDraggingRef.current = true;

    const { x, y } = screenToSvg(e.clientX, e.clientY);
    const nearest = findNearestSlot(x, y, slots, 42);

    if (
      nearest &&
      nearest.key !== start.sourceSlotKey &&
      canDropPlayerInSlot(start.player, nearest)
    ) {
      onMovePlayer?.(start.player, nearest);
    }
  }, [screenToSvg, slots, onMovePlayer, onReorderPlayers]);

  const handleSvgMouseLeave = useCallback(() => {
    if (listReorderDragRef.current) {
      listReorderDragRef.current = null;
      setListReorderDrag(null);
      // Also clear slot-to-slot state in case it was primed alongside the pending reorder
      if (!isDraggingRef.current) {
        dragStartRef.current = null;
      }
      return;
    }
    if (!isDraggingRef.current) return;
    dragStartRef.current = null;
    isDraggingRef.current = false;
    setDragState(null);
    setDragTargetKey(null);
  }, []);

  return (
    <PitchWrapper>
      <div ref={tutorialPitchRef}>
        <PitchTitleRow>
          <PitchTitle>
            {formationName
              ? t("shadowTeam.formationLabel", { name: formationName })
              : t("shadowTeam.formation")}
          </PitchTitle>
          {onExport ? (
            <ExportButton
              ref={tutorialExportRef}
              onClick={onExport}
              disabled={isExporting}
              title={t("shadowTeam.exportPdf")}
            >
              {isExporting ? (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="10 5" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7 1v8M4 6l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 10v1a1 1 0 001 1h8a1 1 0 001-1v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              )}
              {t("shadowTeam.exportPdf")}
            </ExportButton>
          ) : null}
        </PitchTitleRow>

        <PitchCanvas>
          <PitchFrame>
            <PitchTrackerSvg
              ref={svgRef}
              viewBox={`0 0 ${FIELD_WIDTH} ${FIELD_HEIGHT}`}
              preserveAspectRatio="xMidYMid meet"
              width="100%"
              height="100%"
              $drawing={false}
              $grassStripeDirection="horizontal"
              onMouseMove={handleSvgMouseMove}
              onMouseUp={handleSvgMouseUp}
              onMouseLeave={handleSvgMouseLeave}
              style={{ userSelect: "none" }}
            >
            <g transform={`translate(${FIELD_WIDTH} 0) rotate(90)`}>
              <EventPitchBaseLayer
                fieldWidth={FIELD_HEIGHT}
                fieldHeight={FIELD_WIDTH}
                fieldPadding={0}
              />
            </g>

            {slots.map((slot) => {
              const players = slotPlayersMap[slot.key] || [];
              // Only the top N players are rendered on the pitch; all 5 remain in the roster below
              const pitchPlayers = players.slice(0, MAX_PITCH_PLAYERS_VISIBLE_PER_SLOT);
              const cx = (slot.x * FIELD_WIDTH) / 100;
              const cy = (slot.y * FIELD_HEIGHT) / 100;
              const baseRadius = getSlotRadius(slot);
              const slotTitle = getSlotTitle(t, slot);
              const primaryPlayer = pitchPlayers[0] || null;
              const initials = primaryPlayer ? getPlayerInitials(primaryPlayer) : null;
              const hasPhoto = !!primaryPlayer?.photo;

              // Position label pill
              const pillH = 17;
              const pillW = Math.max(30, slot.shortLabel.length * 7.2 + 14);
              const pillY = cy + baseRadius + 5;

              // Player list layout
              const ROW_H = 20;
              const isGoalkeeperSlot = slot.lineIndex < 0;
              // Row width narrows for crowded lines so adjacent slots don't overlap
              const rowW =
                isGoalkeeperSlot ? 155
                : slot.lineSize <= 2 ? 138
                : slot.lineSize === 3 ? 118
                : slot.lineSize === 4 ? 98
                : 84;
              const isBottomSlot = slot.y > 80;
              const listPlacement = isGoalkeeperSlot ? "below" : isBottomSlot ? "above" : "below";
              const listStartY = listPlacement === "above"
                ? cy - baseRadius - 8 - pitchPlayers.length * ROW_H
                : pillY + pillH + (isGoalkeeperSlot ? 4 : 6);

              const isSourceSlot = dragState?.sourceSlotKey === slot.key;
              const isTargetSlot = dragTargetKey === slot.key;
              const draggedPlayerId = dragState?.player?.id;

              return (
                <g
                  key={slot.key}
                  data-testid={`shadow-slot-${slot.key}`}
                  data-list-placement={players.length ? listPlacement : "none"}
                  onClick={() => {
                    if (wasJustDraggingRef.current) {
                      wasJustDraggingRef.current = false;
                      return;
                    }
                    if (!primaryPlayer) {
                      onEmptySlotClick?.(slot);
                    } else {
                      onAddPlayer?.(slot);
                    }
                  }}
                  style={{
                    cursor: dragState
                      ? (isSourceSlot ? "grabbing" : isTargetSlot ? "copy" : "default")
                      : "pointer",
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={slotTitle}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      if (!primaryPlayer) {
                        onEmptySlotClick?.(slot);
                      } else {
                        onAddPlayer?.(slot);
                      }
                    }
                  }}
                >
                  <title>{slotTitle}</title>

                  {/* Drop target highlight ring */}
                  {isTargetSlot && (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={baseRadius + 6}
                      fill="none"
                      stroke={_isLight ? "rgba(35,99,247,0.6)" : "rgba(255,255,255,0.6)"}
                      strokeWidth={2}
                      strokeDasharray="4 2"
                    />
                  )}

                  {/* Main slot circle — fades only if the primary player is being dragged */}
                  {primaryPlayer ? (
                    <g
                      data-testid={`shadow-slot-primary-anchor-${slot.key}`}
                      data-tooltip-id="shadow-player-tooltip"
                      data-player-id={primaryPlayer.id}
                      onMouseEnter={() => {
                        if (!isDraggingRef.current) {
                          onPlayerHover?.(primaryPlayer.id);
                        }
                      }}
                      onMouseDown={(event) => handleSlotMouseDown(event, slot, primaryPlayer)}
                      style={{
                        cursor: dragState
                          ? (isSourceSlot && draggedPlayerId === primaryPlayer.id
                              ? "grabbing"
                              : "grab")
                          : "grab",
                      }}
                    >
                      <g
                        style={{
                          opacity:
                            isSourceSlot && draggedPlayerId === primaryPlayer?.id ? 0.3 : 1,
                        }}
                      >
                        {hasPhoto ? (
                          <>
                            <defs>
                              <clipPath id={`clip-slot-${slot.key.replace(":", "-")}`}>
                                <circle cx={cx} cy={cy} r={baseRadius - 1.5} />
                              </clipPath>
                            </defs>
                            <image
                              href={primaryPlayer.photo}
                              x={cx - baseRadius + 1.5}
                              y={cy - baseRadius + 1.5}
                              width={(baseRadius - 1.5) * 2}
                              height={(baseRadius - 1.5) * 2}
                              clipPath={`url(#clip-slot-${slot.key.replace(":", "-")})`}
                              preserveAspectRatio="xMidYMid slice"
                            />
                          </>
                        ) : (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={baseRadius - 1.5}
                            fill={_isLight ? "rgba(230,235,245,0.95)" : "rgba(13,22,15,0.95)"}
                          />
                        )}
                        <circle
                          cx={cx}
                          cy={cy}
                          r={baseRadius}
                          fill="none"
                          stroke={theme.green}
                          strokeWidth={2}
                        />
                        {!hasPhoto && (
                          <text
                            data-testid={`shadow-slot-label-${slot.key}`}
                            x={cx}
                            y={cy + 1}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill={theme.white}
                            style={{
                              fontSize: initials.length > 2 ? 9 : 11,
                              fontFamily: "SatoshiBold",
                              pointerEvents: "none",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {initials}
                          </text>
                        )}
                      </g>
                    </g>
                  ) : (
                    <g>
                      <circle
                        cx={cx}
                        cy={cy}
                        r={baseRadius - 1.5}
                        fill={_isLight ? "rgba(255,255,255,0.85)" : "rgba(10,10,10,0.82)"}
                      />
                      <circle
                        cx={cx}
                        cy={cy}
                        r={baseRadius}
                        fill="none"
                        stroke={theme.blueish2}
                        strokeWidth={2}
                      />
                      <text
                        data-testid={`shadow-slot-label-${slot.key}`}
                        x={cx}
                        y={cy + 1}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill={theme.blueish2}
                        style={{
                          fontSize: 16,
                          fontFamily: "SatoshiBold",
                          pointerEvents: "none",
                        }}
                      >
                        +
                      </text>
                    </g>
                  )}

                  {/* Position label pill — simple, no "+N" */}
                  <rect
                    x={cx - pillW / 2} y={pillY}
                    width={pillW} height={pillH} rx={pillH / 2}
                    fill={_isLight ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.55)"}
                    stroke={_isLight ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.25)"}
                    strokeWidth={0.75}
                  />
                  <text
                    x={cx} y={pillY + pillH / 2 + 1}
                    textAnchor="middle" dominantBaseline="middle"
                    fill={theme.white}
                    style={{ fontSize: 9, fontFamily: "SatoshiBold", pointerEvents: "none" }}
                  >
                    {slot.shortLabel}
                  </text>

                  {/* ── Player list ── */}
                  {(() => {
                    const isReorderingThisSlot = listReorderDrag?.slotKey === slot.key;
                    const reorderTargetIdx = isReorderingThisSlot ? listReorderDrag.targetIdx : -1;
                    const AVATAR_R = 8;
                    const avatarX = cx - rowW / 2 + 6 + AVATAR_R;
                    // chars available after avatar + order col
                    const maxNameChars = Math.max(7, Math.floor((rowW - AVATAR_R * 2 - 30) / 5.2));
                    const canAddMore = players.length < MAX_SHADOW_TEAM_PLAYERS_PER_SLOT;

                    return (
                      <>
                        {pitchPlayers.map((player, idx) => {
                          const rowCy = listStartY + idx * ROW_H + ROW_H / 2;
                          const fullName = getPlayerFullName(player);
                          const nameLabel = fullName.length > maxNameChars
                            ? `${fullName.slice(0, maxNameChars)}…`
                            : fullName;
                          const isDraggedRow = listReorderDrag
                            ? listReorderDrag.player?.id === player.id && listReorderDrag.slotKey === slot.key
                            : draggedPlayerId === player.id;
                          const showInsertAbove = isReorderingThisSlot && reorderTargetIdx === idx && listReorderDrag.fromIdx !== idx;
                          const isPrimary = idx === 0;
                          const rowBg = _isLight
                            ? (isPrimary ? "rgba(255,255,255,0.94)" : "rgba(255,255,255,0.80)")
                            : (isPrimary ? "rgba(15, 15, 15, 0.92)" : "rgba(15, 15, 15, 0.78)");
                          const rowStroke = _isLight
                            ? (isPrimary ? "rgba(35,99,247,0.35)" : "rgba(0,0,0,0.10)")
                            : (isPrimary ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.12)");
                          const clipId = `clip-row-${slot.key.replace(":", "-")}-${idx}`;

                          return (
                            <g key={player.id}>
                              {/* Insertion indicator */}
                              {showInsertAbove && (
                                <rect
                                  x={cx - rowW / 2}
                                  y={rowCy - ROW_H / 2}
                                  width={rowW}
                                  height={2}
                                  rx={1}
                                  fill="#2363F7"
                                  style={{ pointerEvents: "none" }}
                                />
                              )}

                              <g
                                data-testid={`shadow-slot-player-row-${slot.key}-${idx}`}
                                data-tooltip-id="shadow-player-tooltip"
                                data-player-id={player.id}
                                onMouseEnter={() => {
                                  if (!isDraggingRef.current) onPlayerHover?.(player.id);
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  handleRowMouseDown(e, slot, player, idx, pitchPlayers);
                                }}
                                style={{
                                  cursor: dragState ? (isDraggedRow ? "grabbing" : "default") : "grab",
                                  opacity: isDraggedRow ? 0.25 : 1,
                                  transition: isDraggedRow ? "none" : "opacity 0.15s ease",
                                }}
                              >
                                {/* Row body */}
                                <rect
                                  x={cx - rowW / 2}
                                  y={rowCy - ROW_H / 2 + 1}
                                  width={rowW}
                                  height={ROW_H - 2}
                                  rx={5}
                                  fill={rowBg}
                                  stroke={rowStroke}
                                  strokeWidth={0.75}
                                />

                                {/* Primary accent bar on left edge */}
                                {isPrimary && (
                                  <rect
                                    x={cx - rowW / 2}
                                    y={rowCy - ROW_H / 2 + 2}
                                    width={2.5}
                                    height={ROW_H - 4}
                                    rx={1.5}
                                    fill={_isLight ? "rgba(35,99,247,0.7)" : "rgba(255,255,255,0.7)"}
                                    style={{ pointerEvents: "none" }}
                                  />
                                )}

                                {/* Mini avatar */}
                                {player.photo ? (
                                  <>
                                    <defs>
                                      <clipPath id={clipId}>
                                        <circle cx={avatarX} cy={rowCy} r={AVATAR_R} />
                                      </clipPath>
                                    </defs>
                                    <image
                                      href={player.photo}
                                      x={avatarX - AVATAR_R}
                                      y={rowCy - AVATAR_R}
                                      width={AVATAR_R * 2}
                                      height={AVATAR_R * 2}
                                      clipPath={`url(#${clipId})`}
                                      preserveAspectRatio="xMidYMid slice"
                                      style={{ pointerEvents: "none" }}
                                    />
                                  </>
                                ) : (
                                  <circle
                                    cx={avatarX}
                                    cy={rowCy}
                                    r={AVATAR_R}
                                    fill={_isLight
                                      ? (isPrimary ? "rgba(35,99,247,0.15)" : "rgba(0,0,0,0.08)")
                                      : (isPrimary ? "rgba(60,120,60,0.5)" : "rgba(40,40,50,0.6)")}
                                    style={{ pointerEvents: "none" }}
                                  />
                                )}
                                {/* Avatar border ring */}
                                <circle
                                  cx={avatarX}
                                  cy={rowCy}
                                  r={AVATAR_R}
                                  fill="none"
                                  stroke={isPrimary ? `${pitchAccent},0.4)` : "rgba(255,255,255,0.1)"}
                                  strokeWidth={0.75}
                                  style={{ pointerEvents: "none" }}
                                />

                                {/* Order number */}
                                <text
                                  x={cx - rowW / 2 + AVATAR_R * 2 + 11}
                                  y={rowCy + 0.5}
                                  textAnchor="start"
                                  dominantBaseline="middle"
                                  fill={isPrimary ? `${pitchAccent},0.9)` : "rgba(255,255,255,0.3)"}
                                  style={{
                                    fontSize: 7,
                                    fontFamily: "SatoshiBold",
                                    pointerEvents: "none",
                                  }}
                                >
                                  {idx + 1}.
                                </text>

                                {/* Player name */}
                                <text
                                  x={cx + 12}
                                  y={rowCy + 0.5}
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  fill={isPrimary ? theme.white : (_isLight ? "rgba(60,70,90,0.85)" : "rgba(200,210,200,0.75)")}
                                  style={{
                                    fontSize: isPrimary ? 8.5 : 8,
                                    fontFamily: isPrimary ? "SatoshiBold" : "SatoshiMedium",
                                    pointerEvents: "none",
                                    paintOrder: "stroke",
                                    stroke: _isLight ? "none" : "rgba(0,0,0,0.8)",
                                    strokeWidth: _isLight ? 0 : 2,
                                    strokeLinejoin: "round",
                                  }}
                                >
                                  {nameLabel}
                                </text>
                              </g>
                            </g>
                          );
                        })}

                        {/* Insertion line at end */}
                        {isReorderingThisSlot && reorderTargetIdx >= pitchPlayers.length && (
                          <rect
                            x={cx - rowW / 2}
                            y={listStartY + pitchPlayers.length * ROW_H - 1}
                            width={rowW}
                            height={2}
                            rx={1}
                            fill={_isLight ? "#2363F7" : "#ccff00"}
                            style={{ pointerEvents: "none" }}
                          />
                        )}

                        {/* "+" add row — only if pitch is not already showing max visible AND slot has room */}
                        {pitchPlayers.length > 0 && canAddMore && listPlacement !== "above" && (
                          (() => {
                            const addRowCy = listStartY + pitchPlayers.length * ROW_H + ROW_H / 2;
                            return (
                              <g
                                data-testid={`shadow-slot-add-row-${slot.key}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (wasJustDraggingRef.current) return;
                                  onAddPlayer?.(slot);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <rect
                                  x={cx - rowW / 2}
                                  y={addRowCy - ROW_H / 2 + 1}
                                  width={rowW}
                                  height={ROW_H - 2}
                                  rx={5}
                                  fill={`${pitchAccent},0.03)`}
                                  stroke={`${pitchAccent},0.2)`}
                                  strokeWidth={0.75}
                                  strokeDasharray="4 3"
                                />
                                <text
                                  x={cx}
                                  y={addRowCy + 1}
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  fill={`${pitchAccent},0.7)`}
                                  style={{
                                    fontSize: 11,
                                    fontFamily: "SatoshiBold",
                                    pointerEvents: "none",
                                  }}
                                >
                                  +
                                </text>
                              </g>
                            );
                          })()
                        )}
                      </>
                    );
                  })()}
                </g>
              );
            })}

            {/* Drag ghost — rendered last so it's always on top */}
            {dragState && (
              <g
                style={{ pointerEvents: "none" }}
                transform={`translate(${dragState.x}, ${dragState.y})`}
              >
                {/* Outer glow ring */}
                <circle
                  cx={0}
                  cy={0}
                  r={SLOT_RADIUS + 6}
                  fill="none"
                  stroke={_isLight ? "rgba(35,99,247,0.35)" : "rgba(255,255,255,0.25)"}
                  strokeWidth={1.5}
                />
                {/* Circle body */}
                {dragState.player?.photo ? (
                  <>
                    <defs>
                      <clipPath id="drag-ghost-clip">
                        <circle cx={0} cy={0} r={SLOT_RADIUS - 1.5} />
                      </clipPath>
                    </defs>
                    <image
                      href={dragState.player.photo}
                      x={-(SLOT_RADIUS - 1.5)}
                      y={-(SLOT_RADIUS - 1.5)}
                      width={(SLOT_RADIUS - 1.5) * 2}
                      height={(SLOT_RADIUS - 1.5) * 2}
                      clipPath="url(#drag-ghost-clip)"
                      preserveAspectRatio="xMidYMid slice"
                    />
                  </>
                ) : (
                  <circle
                    cx={0}
                    cy={0}
                    r={SLOT_RADIUS - 1.5}
                    fill={_isLight ? "rgba(230,235,245,0.95)" : "rgba(13,22,15,0.95)"}
                  />
                )}
                {/* Green border ring */}
                <circle
                  cx={0}
                  cy={0}
                  r={SLOT_RADIUS}
                  fill="none"
                  stroke={theme.green}
                  strokeWidth={2.5}
                />
                {/* Initials (if no photo) */}
                {dragState.player && !dragState.player.photo && (
                  <text
                    x={0}
                    y={1}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={theme.white}
                    style={{ fontSize: 11, fontFamily: "SatoshiBold" }}
                  >
                    {getPlayerInitials(dragState.player)}
                  </text>
                )}
              </g>
            )}
            </PitchTrackerSvg>

          </PitchFrame>

          {/* ── Quick-add inline popover — lives outside PitchFrame so overflow:hidden doesn't clip it ── */}
          {quickAddSlot && (() => {
            const isLeft = quickAddSlot.x < 28;
            const isRight = quickAddSlot.x > 72;
            const isBottom = quickAddSlot.y > 70;
            const hShift = isLeft ? "0%" : isRight ? "-100%" : "-50%";
            const vShift = isBottom ? "calc(-100% - 26px)" : "26px";
            return (
              <QuickAddOverlay
                ref={popoverRef}
                style={{
                  left: `${quickAddSlot.x}%`,
                  top: `${quickAddSlot.y}%`,
                  transform: `translate(${hShift}, ${vShift})`,
                }}
              >
                <QuickAddSlotLabel>
                  {t("shadowTeam.quickAddLabel", { position: quickAddSlot.shortLabel })}
                </QuickAddSlotLabel>

                <QuickAddInput
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  value={quickSearchValue}
                  placeholder={t("shadowTeam.quickAddPlaceholder")}
                  onChange={handleQuickSearchInput}
                />

                <QuickAddResults>
                  {quickAddLoading && (
                    <QuickAddHint>{t("shadowTeam.searching")}</QuickAddHint>
                  )}
                  {!quickAddLoading && filteredQuickAddResults.length === 0 && (
                    <QuickAddHint>{t("shadowTeam.quickAddEmptyHint")}</QuickAddHint>
                  )}
                  {!quickAddLoading &&
                    filteredQuickAddResults.map((player) => {
                      const rawNat = Array.isArray(player.nationality)
                        ? player.nationality[0]
                        : null;
                      const playerPhoto = getPlayerPhoto(player);
                      return (
                        <QuickAddResultItem
                          key={player.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => onQuickAddSelect?.(player)}
                        >
                          <QuickAddPlayerAvatar
                            src={getMediaUrl(playerPhoto)}
                            alt=""
                            onError={(e) => {
                              e.currentTarget.src = getMediaUrl(DEFAULT_PLAYER_PHOTO);
                            }}
                          />
                          <QuickAddPlayerName>
                            {getPlayerFullName(player)}
                          </QuickAddPlayerName>
                          {rawNat?.short_name && (
                            <QuickAddPlayerNat>{rawNat.short_name}</QuickAddPlayerNat>
                          )}
                        </QuickAddResultItem>
                      );
                    })}
                </QuickAddResults>

                <QuickAddFooter
                  type="button"
                  onClick={() => {
                    onQuickAddClose?.();
                    onAddPlayer?.(quickAddSlot);
                  }}
                >
                  {t("shadowTeam.quickAddOpenModal")}
                </QuickAddFooter>
              </QuickAddOverlay>
            );
          })()}
        </PitchCanvas>

        <PitchHint>{t("shadowTeam.pitchHint")}</PitchHint>
      </div>

      <RosterSection ref={tutorialRosterRef}>
        <RosterTitle>{t("shadowTeam.slotRosterTitle")}</RosterTitle>
        <RosterHint>{t("shadowTeam.slotRosterHint")}</RosterHint>

        {(() => {
          // Group slots by lineIndex, preserving order within each group
          const groupMap = {};
          const lineOrder = [];
          slots.forEach((slot) => {
            const key = slot.lineIndex;
            if (!groupMap[key]) {
              groupMap[key] = { lineIndex: key, band: slot.band, slots: [] };
              lineOrder.push(key);
            }
            groupMap[key].slots.push(slot);
          });

          // Sort: highest lineIndex first (attack → defense), GK (-1) last
          lineOrder.sort((a, b) => {
            if (a === -1) return 1;
            if (b === -1) return -1;
            return b - a;
          });

          return lineOrder.map((lineIndex) => {
            const group = groupMap[lineIndex];
            const isGk = lineIndex < 0;
            const sectionLabel = getRosterLineSectionLabel(lineIndex, group.band);

            return (
              <RosterLineSection key={`line-${lineIndex}`}>
                <RosterLineSectionLabel>{sectionLabel}</RosterLineSectionLabel>

                <RosterLineGrid $isGk={isGk}>
                  {group.slots.map((slot) => {
                    const players = slotPlayersMap[slot.key] || [];
                    const canAddMore = players.length < MAX_SHADOW_TEAM_PLAYERS_PER_SLOT;

                    return (
                      <PosCard
                        key={`card-${slot.key}`}
                        data-testid={`shadow-roster-slot-${slot.key}`}
                        $isDragTarget={rosterDragOver === slot.key}
                        $isDragSource={rosterDrag?.sourceSlotKey === slot.key}
                        onDragOver={(e) => handleRosterDragOver(e, slot.key)}
                        onDragLeave={handleRosterDragLeave}
                        onDrop={(e) => handleRosterDrop(e, slot)}
                      >
                        <PosCardHeader>
                          <PosBadge>{slot.shortLabel}</PosBadge>
                          <PosCount $empty={players.length === 0}>
                            {players.length > 0 ? (
                              <>
                                <PosCountDot />
                                {players.length === 1 ? "1 player" : `${players.length} players`}
                              </>
                            ) : (
                              "Empty"
                            )}
                          </PosCount>
                        </PosCardHeader>

                        <PosCardBody>
                          {players.length > 0 ? (
                            players.map((player, index) => {
                              const cachedPlayerData =
                                playerStatsCache?.[player?.id]?.data?.player ?? null;
                              const cachedTeamName = cachedPlayerData?.team?.name ?? null;
                              const playerClubName =
                                (cachedTeamName ? String(cachedTeamName).trim() || null : null) ??
                                getPlayerClubName(player);
                              const photoUrl =
                                cachedPlayerData?.photos?.[0]?.full ||
                                cachedPlayerData?.photos?.[0]?.image ||
                                player.photo ||
                                null;

                              return (
                                <PlayerChip
                                  key={`${slot.key}-${player?.id}`}
                                  data-testid={`shadow-roster-player-${slot.key}-${index}`}
                                  draggable
                                  $isDragging={rosterDrag?.player?.id === player?.id}
                                  $isDropTarget={
                                    rosterDragOverPlayer?.slotKey === slot.key &&
                                    rosterDragOverPlayer?.playerId === player?.id
                                  }
                                  data-tooltip-id="shadow-player-tooltip"
                                  data-player-id={player?.id}
                                  onMouseEnter={() => {
                                    if (!rosterDrag) onPlayerHover?.(player?.id);
                                  }}
                                  onDragStart={(e) => handleRosterDragStart(e, player, slot.key)}
                                  onDragEnd={handleRosterDragEnd}
                                  onDragEnter={() => handleRosterPlayerDragEnter(slot.key, player?.id)}
                                  onDragOver={(e) => handleRosterPlayerDragOver(e, slot.key, player?.id)}
                                  onDragLeave={(e) => handleRosterPlayerDragLeave(e, slot.key, player?.id)}
                                  onDrop={(e) => handleRosterPlayerDrop(e, slot, player)}
                                >
                                  <PlayerChipAvatar>
                                    {getPlayerInitials(player)}
                                    {photoUrl ? (
                                      <img
                                        src={getMediaUrl(photoUrl)}
                                        alt=""
                                        style={{
                                          position: "absolute",
                                          inset: 0,
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                          objectPosition: "center top",
                                        }}
                                        onError={(e) => {
                                          e.currentTarget.style.display = "none";
                                        }}
                                      />
                                    ) : null}
                                  </PlayerChipAvatar>
                                  <PlayerChipInfo>
                                    <PlayerChipName
                                      href={`/tournaments/player/${player?.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {getPlayerFullName(player)}
                                    </PlayerChipName>
                                    {playerClubName ? (
                                      <PlayerChipClub>{playerClubName}</PlayerChipClub>
                                    ) : null}
                                  </PlayerChipInfo>
                                  <ChipTrashBtn
                                    type="button"
                                    title={t("shadowTeam.removePlayer")}
                                    aria-label={t("shadowTeam.removePlayer")}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      onRemovePlayer?.(player);
                                    }}
                                  >
                                    <TrashIconSvg aria-hidden="true" focusable="false" />
                                  </ChipTrashBtn>
                                </PlayerChip>
                              );
                            })
                          ) : (
                            <RosterEmptyState>
                              {t("shadowTeam.positionEmptyShort")}
                            </RosterEmptyState>
                          )}

                          {canAddMore && (
                            <RosterAddBtn
                              type="button"
                              data-testid={`shadow-roster-add-${slot.key}`}
                              aria-label={t("shadowTeam.openSlotAction", { shortLabel: slot.shortLabel })}
                              onClick={() => onAddPlayer?.(slot)}
                            >
                              <span style={{ fontSize: 14, lineHeight: 1, fontFamily: "SatoshiRegular" }}>+</span>
                              Add player
                            </RosterAddBtn>
                          )}
                        </PosCardBody>
                      </PosCard>
                    );
                  })}
                </RosterLineGrid>
              </RosterLineSection>
            );
          });
        })()}
      </RosterSection>

      <Tooltip
        id="shadow-player-tooltip"
        place="top"
        delayShow={220}
        delayHide={80}
        style={{ background: "transparent", padding: 0, zIndex: 9999 }}
        render={({ activeAnchor }) => {
          const pid = Number(activeAnchor?.getAttribute?.("data-player-id"));
          if (!pid) return null;

          const player = findPlayerById(slotPlayersMap, pid);
          if (!player) return null;

          const statsEntry = playerStatsCache[pid];
          const isStatsLoading = !statsEntry || statsEntry.loading;
          const sp = statsEntry?.data?.player || null;

          if (isStatsLoading) {
            return (
              <TooltipCard>
                <TooltipLoadingText>{t("shadowTeam.tooltipLoading")}</TooltipLoadingText>
              </TooltipCard>
            );
          }

          if (!sp) {
            return null;
          }

          const photo =
            sp?.photos?.[0]?.full ||
            sp?.photos?.[0]?.image ||
            player.photo ||
            DEFAULT_PLAYER_PHOTO;

          const nationality =
            sp?.nationality?.[0] ||
            (player.nationality
              ? {
                  name: player.nationality.name,
                  short_name: player.nationality.shortName,
                  flag: player.nationality.flag,
                }
              : null);

          // All data comes directly from the player object (already in the team response)
          const age = Number.isFinite(Number(sp?.age))
            ? Number(sp.age)
            : player.age ?? getAge(player.birthDate);
          const positions = Array.isArray(sp?.positions)
            ? sp.positions
            : Array.isArray(sp?.position)
            ? sp.position
            : [];
          const position = positions[0]?.name || player.position;
          const teamName = sp?.team?.name || player.team?.name;
          const contractYear = getContractYear(
            sp?.contract?.until || sp?.contract_until || player.contractUntil
          );
          const { seasonStats } = player;
          const hasSeasonStats =
            seasonStats?.goals != null ||
            seasonStats?.assists != null ||
            seasonStats?.minutesPlayed != null ||
            seasonStats?.matchesPlayed != null;

          return (
            <TooltipCard>
              <TooltipHeader>
                <TooltipPlayerAvatar
                  src={getMediaUrl(photo)}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.src = DEFAULT_PLAYER_PHOTO;
                  }}
                />
                <TooltipPlayerInfo>
                  <TooltipPlayerName>
                    {player.displayName || getPlayerFullName(player)}
                  </TooltipPlayerName>
                  {nationality && (
                    <TooltipNationality>
                      {nationality.flag && (
                        <TooltipNatFlag
                          src={getMediaUrl(nationality.flag)}
                          alt={nationality.name || ""}
                        />
                      )}
                      <TooltipNatName>
                        {nationality.short_name || nationality.name || ""}
                      </TooltipNatName>
                    </TooltipNationality>
                  )}
                </TooltipPlayerInfo>
              </TooltipHeader>

              {(position || teamName || age !== null || contractYear !== null) && (
                <>
                  <TooltipDivider />
                  <TooltipStats>
                    {position && (
                      <TooltipStatRow>
                        <TooltipStatLabel>Position</TooltipStatLabel>
                        <TooltipStatValue>{position}</TooltipStatValue>
                      </TooltipStatRow>
                    )}
                    {teamName && (
                      <TooltipStatRow>
                        <TooltipStatLabel>Club</TooltipStatLabel>
                        <TooltipStatValue>{teamName}</TooltipStatValue>
                      </TooltipStatRow>
                    )}
                    {age !== null && (
                      <TooltipStatRow>
                        <TooltipStatLabel>Age</TooltipStatLabel>
                        <TooltipStatValue>{age} yrs</TooltipStatValue>
                      </TooltipStatRow>
                    )}
                    {contractYear !== null && (
                      <TooltipStatRow>
                        <TooltipStatLabel>Contract</TooltipStatLabel>
                        <TooltipStatValueGreen>{contractYear}</TooltipStatValueGreen>
                      </TooltipStatRow>
                    )}
                  </TooltipStats>
                </>
              )}

              {hasSeasonStats && seasonStats && (
                <>
                  <TooltipDivider />
                  <TooltipSectionLabel>Season stats</TooltipSectionLabel>
                  <TooltipSeasonStats>
                    <TooltipSeasonStat>
                      <TooltipSeasonStatValue $green>
                        {seasonStats.goals ?? "—"}
                      </TooltipSeasonStatValue>
                      <TooltipSeasonStatLabel>Goals</TooltipSeasonStatLabel>
                    </TooltipSeasonStat>
                    <TooltipSeasonStat>
                      <TooltipSeasonStatValue>
                        {seasonStats.assists ?? "—"}
                      </TooltipSeasonStatValue>
                      <TooltipSeasonStatLabel>Assists</TooltipSeasonStatLabel>
                    </TooltipSeasonStat>
                    <TooltipSeasonStat>
                      <TooltipSeasonStatValue $green>
                        {seasonStats.minutesPlayed ?? "—"}
                      </TooltipSeasonStatValue>
                      <TooltipSeasonStatLabel>Mins played</TooltipSeasonStatLabel>
                    </TooltipSeasonStat>
                    <TooltipSeasonStat>
                      <TooltipSeasonStatValue>
                        {seasonStats.matchesPlayed ?? "—"}
                      </TooltipSeasonStatValue>
                      <TooltipSeasonStatLabel>Games played</TooltipSeasonStatLabel>
                    </TooltipSeasonStat>
                  </TooltipSeasonStats>
                </>
              )}
            </TooltipCard>
          );
        }}
      />
    </PitchWrapper>
  );
};

export default ShadowTeamPitch;
